'use strict';

define('vb/private/stateManagement/switcherRootMixin',[
  'vb/private/stateManagement/switcherRootRouter',
  'vb/helpers/mixin',
  'vb/private/stateManagement/switcherFlowMixin',
  'vb/private/stateManagement/switcherAppUiMixin',
], (SwitcherRootRouter, Mixin, SwitcherFlowMixin, SwitcherAppUiMixin) => {
  /**
   * Implementation of the special behavior of a root page for a switcher element.
   * Each switcher element has a root page and it is created its constructor.
   * A root page is fake page container that does the transition between the App UI
   * where the switcher component is and the App UI inside the switcher element.
   * This fake page is not complete, for example it does not have a scope object.
   */
  const SwitcherRootMixin = (superclass) => class extends superclass {
    constructor(switcherElement, switcher) {
      super(`switcher-${switcherElement.id}`, switcher.parent, '', 'SwitcherPage');
      this.switcher = switcher;

      // Replace the moduleConfig of the page with the one used by the ojModule of the switcher
      // Can be enumerated but is readonly and cannot be replaced
      Object.defineProperty(this, 'moduleConfig', {
        get: () => switcher.moduleConfigs[switcherElement.index],
        enumerable: true,
      });

      // This switcher root page does not have a definition, so nothing to initialize
      this.initializePromise = Promise.resolve();

      this._rootRouter = new SwitcherRootRouter(this.switcher, switcherElement, this.fullPath);
    }

    get rootPage() {
      return this;
    }

    get rootRouter() {
      return this._rootRouter;
    }

    isNavigationBoundary() {
      return true;
    }

    /**
     * Determines if the page page has been initialized. This is only true after
     * the root has been navigated successfully the first time and the moduleConfig
     * observable is set to the leaf page.
     *
     * @return {boolean}  True if initialized, False otherwise.
     */
    isInitialized() {
      return this.moduleConfig().pagePath;
    }

    createRouter() {
      return this._rootRouter;
    }

    createFlow(id) {
      if (this.application.doesAppUiExist(id)) {
        const extension = this.application.appUiInfos.getExtension(id);

        if (!extension) {
          throw new Error(`App UI ${id} metadata is available but its extension has not been loaded.`);
        }
        const { AppUiClass } = this.application.constructor;
        return new (Mixin(AppUiClass).with(SwitcherAppUiMixin))(extension, extension.getAppUiInfo(id), this);
      }

      const { FlowClass } = this.parent.getParentFlow().constructor;

      // Construct a Flow (either Flow or PackageFlow depending the environment) with the behavior
      // required for working in the switcher component.
      return new (Mixin(FlowClass).with(SwitcherFlowMixin))(id, this);
    }

    /**
     * Retrieve the leaf page instance when navigating between 2 App UIs.
     * See other implementation in application.js
     *
     * Called by navigateOperation
     *
     * @param  {String} appUiId the id of the App UI to navigate to
     * @param  {Object} navContext the context of the current navigation chain
     * @return {Promise} a Promise resolving to the leaf page instance
     */
    async getLeafPageInstanceFromAppUiId(appUiId, navContext) {
      const resolvedAppUiId = this.application.appUiInfos.getAppUiIdFromUrlId(appUiId) || appUiId;

      // A partial digest is selected by App UI, but its runtime and RequireJS entries describe the complete
      // owning extension (including all of that extension's App UIs and files). Once the target App UI already
      // has an owning extension, switching to it needs no additional partial-digest request; refetching would
      // only reapply the same extension metadata.
      if (!this.application.appUiInfos.getExtension(resolvedAppUiId)) {
        if (await this.application.extensionRegistry.loadAppUiDigest(resolvedAppUiId)) {
          await this.application.loadExtensions();
        }
      }
      await this.application.ensureHostRootPageExtensionAvailable(resolvedAppUiId);

      // Navigation to an app-flow is done by the root page
      return this.loadContainer(resolvedAppUiId, navContext)
        .then((appUi) => appUi.loadDefaultPageOrFlow(navContext));
    }

    getParentFlow() {
      // Switcher root page parent is always the page with the switcher component
      return this.parent.getParentFlow();
    }

    /**
     * Build the title that will be used for this page.
     * Walk up the flow hierarchy and gather the title of all pages.
     *
     * @param {String} [title] the base of the title
     * @return {String} the title
     */
    buildTitle(title) {
      return this.parent.buildTitle(title);
    }

    // eslint-disable-next-line class-methods-use-this
    hideFromUrl() {
      return false;
    }

    // eslint-disable-next-line class-methods-use-this
    getNavPath() {
      // return this.urlId;
      return '';
    }

    // eslint-disable-next-line class-methods-use-this
    isDefaultFlowId() {
      return true;
    }

    // eslint-disable-next-line class-methods-use-this
    checkForDirtyData() {
      // Switcher root page does not have variables, so
      // there is never dirty data
      return false;
    }

    // eslint-disable-next-line class-methods-use-this
    isNavigable() {
      return true;
    }

    /**
     * Re-sets the dirty data state of variables in the current container.
     */
    // eslint-disable-next-line class-methods-use-this
    resetDirtyData() {
      // since this is a fake page container and has no scope object, reset dirty data should be a no op
    }

    dispose() {
      this._rootRouter.dispose();
      super.dispose();
    }
  };

  return SwitcherRootMixin;
});

